/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactNode } from 'react';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { ChatResponseField, type ChatResponseFieldRef } from '@coursera-web/cds-labs';
import classNames from 'classnames';

import { useTracker } from '@coursera/event-pulse/react';

import type {
  Props as RichInputFieldProps,
  RichInputFieldRef,
} from 'bundles/ai-coach-platform/components/patterns/chat/common/RichInputField';
import type { SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import SuggestedActionsPopover from 'bundles/ai-coach-platform/components/patterns/popover/SuggestedActionsPopover';
import { CoachAction } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import type { GlobalState } from '../store/types';

type Props = {
  placeholder: string;
  disableInput: boolean;
  allowInputWhileLoading?: boolean;
  disableSpeechToText?: boolean;
  speechToTextDisabledTooltip?: string;
  sendMessage: SendMessageFunc;
  /**
   * @deprecated No longer functional after cds-labs-chat-components gate cleanup. Will be removed once consumers are updated.
   */
  inputVariant?: 'rich' | 'cds-labs';
  /**
   * Custom attachments rendered in the composer's context row (above the text input).
   * Forwarded to ChatResponseField.
   */
  contextAttachments?: ReactNode;
  /**
   * When true, always uses the stacked layout in ChatResponseField (leading action and
   * controls below the textarea). Only applies to the `'cds-labs'` variant.
   */
  multiLineLayout?: boolean;
} & Omit<RichInputFieldProps, 'placeholder'>;

const styles = {
  container: css`
    padding: 0 var(--cds-spacing-200);

    .coach-chat-input-field {
      padding: var(--cds-spacing-200) 0 0 0;
    }
  `,
  cdsLabsContainer: css`
    max-width: 656px;
    width: 100%;
    margin: 0 auto;
  `,
};

/**
 * Wrapper around ChatResponseField that handles the Redux store interaction and eventing for Coach chats
 */
const ChatRichInputField = forwardRef<RichInputFieldRef, Props>((props, richInputRef) => {
  const {
    sendMessage,
    placeholder,
    disableInput,
    allowInputWhileLoading = false,
    disableSpeechToText,
    speechToTextDisabledTooltip,
    allowSendWithEmptyMessage,
    onTextInputChange,
    contextAttachments,
    multiLineLayout,
    ...richInputFieldProps
  } = props;
  const { isMessageLoading, isMessageStreaming } = useSelector((state: GlobalState) => ({
    isMessageLoading: state.behavior.messageLoader,
    isMessageStreaming: state.behavior.isMessageStreaming,
  }));

  const refContainer = useRef<HTMLDivElement>(null);
  const responseFieldRef = useRef<ChatResponseFieldRef>(null);
  useImperativeHandle(richInputRef, () => ({
    focus: () => responseFieldRef.current?.focus(),
  }));
  const [inputText, setInputText] = useState('');
  const [listening, setListening] = useState(false);
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const handleSendMessageClick = (message: string) => {
    // ignore empty messages unless the parent has external content to commit
    if (!message && !allowSendWithEmptyMessage) {
      return;
    }

    sendMessage(message, CoachAction.TypedMessage);

    if (trackingContext) {
      track('interact_coach', {
        coachInteractionType: 'send_message',
        coach: {
          mode: trackingContext.mode,
          location: 'chat',
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
            type: trackingContext.itemType,
          },
        }),
        ...(trackingContext.form && { form: trackingContext.form }),
      });

      // This allows us to send the input message to amplitude as a separate event all together.
      // The main goal for now being to derive the character count of the input text as requested by product.
      track('send_coach_message', {
        coachMessageText: message,
        coachMessageLength: message.length,
        coach: {
          mode: trackingContext.mode,
        },
        ...(trackingContext.courseId && {
          product: {
            type: 'course',
            id: trackingContext.courseId as string,
          },
        }),
        ...(trackingContext.itemId && {
          courseItem: {
            id: trackingContext.itemId as string,
            type: trackingContext.itemType,
          },
        }),
        ...(trackingContext.form && { form: trackingContext.form }),
      });
    }
  };

  // The cds-labs ChatResponseField is presentational: this wrapper keeps owning the
  // Redux-driven disabled/streaming state and the send eventing (handleSendMessageClick).
  // ChatResponseField does not clear itself on send in uncontrolled mode, so we clear it
  // via its ref after each send. Note: it has no speech-to-text and no stop button (no
  // platform stop action).
  const isLoadingOrStreaming = isMessageLoading || isMessageStreaming;
  const isDisabled = disableInput || (!allowInputWhileLoading && isLoadingOrStreaming);
  const isSubmitDisabled = disableInput || isLoadingOrStreaming;

  const handleResponseFieldChange = (value: string) => {
    setInputText(value);
    onTextInputChange?.(value);
  };

  const handleResponseFieldSend = (sendValue: string) => {
    if (isSubmitDisabled) {
      return;
    }

    handleSendMessageClick(sendValue);
    setInputText('');
    responseFieldRef.current?.clear();
  };

  return (
    <div ref={refContainer} css={[styles.container, styles.cdsLabsContainer]}>
      <div
        className={classNames('coach-chat-input-field', {
          'coach-message-disabled': (!allowInputWhileLoading && isLoadingOrStreaming) || disableInput,
        })}
      >
        <ChatResponseField
          ref={responseFieldRef}
          placeholder={placeholder}
          onSend={handleResponseFieldSend}
          onChange={handleResponseFieldChange}
          onMicrophoneStart={() => setListening(true)}
          onMicrophoneStop={() => setListening(false)}
          contextAttachments={contextAttachments}
          disabled={isDisabled}
          disableSubmit={isSubmitDisabled}
          allowEmptySend={allowSendWithEmptyMessage}
          autoFocus={disableInput ? false : richInputFieldProps.autofocus}
          maxLength={richInputFieldProps.maxLength}
          data-testid="coach-chat-rich-input-field"
          aria-label={richInputFieldProps['aria-label'] ?? placeholder}
          value={richInputFieldProps.value}
          disableSpeechToText={disableSpeechToText}
          speechToTextDisabledTooltip={speechToTextDisabledTooltip}
          leadingAction={richInputFieldProps.actionButtons}
          multiLineLayout={multiLineLayout}
          trailingActions={
            richInputFieldProps.suggestedActions ? (
              <SuggestedActionsPopover
                actions={richInputFieldProps.suggestedActions}
                disabled={isDisabled || inputText.length > 0 || listening}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
});

export default ChatRichInputField;
